import { ContextMenu, SVG } from "shared";
import { SVG_ENUM } from "enums";

import { CompanyMyType } from "types/CompanyTypes";
import moment from "moment";
import { getCurrencyFromUuid, getIndustryFromUuid } from "utils";
import { MarketInfoSpeaker } from "features/marketInfo";
import { UnviewedPostCountType } from "types/PostTypes";

const items = ({
  setEditCompany,
  setShowSellShares,
  setShowDelete,
  isOther = false,
  setTransformationModal,
}: {
  setEditCompany: (company: CompanyMyType) => void;
  setShowSellShares?: (company: CompanyMyType) => void;
  setShowDelete?: (company: CompanyMyType) => void;
  isOther?: boolean;
  setTransformationModal: (company: CompanyMyType) => void;
}) => {
  return (company: CompanyMyType) => {
    const isCompanyShare = company?.type === "share";
    const actions = [];
    actions.push({
      name: "Dodaj transakcję",
      action: () => setShowSellShares && setShowSellShares(company),
      svg: SVG_ENUM.COINS_HAND,
    });
    if (company.origin === "other") {
      actions.unshift({
        name: "Edytuj",
        action: () => setEditCompany(company),
        svg: SVG_ENUM.EDIT,
      });
    }
    if (isCompanyShare)
      actions.push({
        name: "Zmień rodzaj spółki",
        action: () => setTransformationModal(company),
        svg: SVG_ENUM.EDIT,
      });
    if (isOther) {
      actions.push({
        name: "Usuń",
        action: () => setShowDelete && setShowDelete(company),
        svg: SVG_ENUM.TRASH,
      });
    }

    return actions;
  };
};

export const mobileColumns = ({
  hasAccessToOtherCompanySpeaker,
  setEditCompany,
  isOther = false,
  setShowSellShares,
  setShowDelete,
  unviewedPostCount,
  setTransformationModal,
}: {
  hasAccessToOtherCompanySpeaker?: boolean | undefined;
  setEditCompany: (company: CompanyMyType) => void;
  isOther?: boolean;
  setShowDelete?: (company: CompanyMyType) => void;
  setShowSellShares?: (company: CompanyMyType) => void;
  unviewedPostCount: UnviewedPostCountType | undefined;
  setTransformationModal: (company: CompanyMyType) => void;
}) => [
  {
    title: "",
    key: "index",
    width: 40,
    render: (value: any, record: CompanyMyType, index: number) => (
      <div
        data-tooltip-id="company-tooltip"
        data-tooltip-content={record.description}
        className="companies-table__info"
      >
        <SVG type={SVG_ENUM.INFO} />
      </div>
    ),
  },
  {
    dataIndex: "name",
    key: "name",
    width: 180,
  },
  {
    title: "",
    dataIndex: "",
    key: "status",
    width: 40,
    render: (value: any, record: CompanyMyType) => (
      <MarketInfoSpeaker
        hasAccess={hasAccessToOtherCompanySpeaker}
        id={record.id}
        postsCount={unviewedPostCount?.[record.id] || 0}
      />
    ),
  },

  {
    width: 40,
    render: (item: CompanyMyType) => (
      <ContextMenu
        data={item}
        items={items({
          setEditCompany,
          setShowSellShares,
          setShowDelete,
          isOther,
          setTransformationModal,
        })(item)}
      >
        <SVG type={SVG_ENUM.DOTS} />
      </ContextMenu>
    ),
  },
];

export const desktopColumns = ({
  hasAccessToOtherCompanySpeaker = true,
  setEditCompany,
  isOther = false,
  setShowSellShares,
  setShowDelete,
  unviewedPostCount,
  setTransformationModal,
}: {
  hasAccessToOtherCompanySpeaker?: boolean | undefined;
  setEditCompany: (company: CompanyMyType) => void;
  setShowDelete?: (company: CompanyMyType) => void;
  isOther?: boolean;
  setShowSellShares?: (company: CompanyMyType) => void;
  unviewedPostCount: UnviewedPostCountType | undefined;
  setTransformationModal: (company: CompanyMyType) => void;
}) => {
  const columns = [
    {
      key: "index",
      width: 40,
      render: (value: any, record: CompanyMyType, index: number) => (
        <div
          data-tooltip-id="company-tooltip"
          data-tooltip-content={record.description}
          className="companies-table__info"
        >
          <SVG type={SVG_ENUM.INFO} />
        </div>
      ),
    },
    {
      title: "Nazwa spółki",
      dataIndex: "name",
      key: "name",
      width: "250px",
      minWidth: 250,
    },
    {
      title: "Branża",
      width: "130px",
      render: (value: any, record: CompanyMyType, index: number) => {
        return `${getIndustryFromUuid(record.industryUuid)}`;
      },
    },
    {
      title: "Ilość sztuk",
      width: 100,
      dataIndex: "sharesCount",
      key: "sharesCount",
    },
    {
      title: "Koszt nabycia",
      width: 130,
      render: (value: any, record: CompanyMyType, index: number) =>
        record.sharesValue
          ? `${record.sharesValue} ${getCurrencyFromUuid(record.currencyUuid)}`
          : "-",
    },
    {
      title: "Waluta",
      width: 60,
      render: (value: any, record: CompanyMyType, index: number) =>
        `${getCurrencyFromUuid(record.currencyUuid)}`,
    },

    {
      title: isOther ? null : "Data publikacji na giełdzie",
      width: 170,
      render: (value: any, record: CompanyMyType, index: number) =>
        record.publicationDate
          ? `${moment(record.publicationDate).format("DD/MM/YYYY")}r.`
          : "",
    },

    {
      title: "",
      dataIndex: "",
      key: "status",
      width: 40,
      render: (value: any, record: CompanyMyType) => (
        <>
          <MarketInfoSpeaker
            hasAccess={hasAccessToOtherCompanySpeaker}
            id={record.id}
            postsCount={unviewedPostCount?.[record.id] || 0}
          />
        </>
      ),
    },
    {
      width: 20,
      render: (item: CompanyMyType) => (
        <ContextMenu
          data={item}
          items={items({
            setEditCompany,
            setShowSellShares,
            setShowDelete,
            isOther,
            setTransformationModal,
          })(item)}
        >
          <SVG type={SVG_ENUM.DOTS} />
        </ContextMenu>
      ),
    },
  ];

  return columns;
};
